import { Input } from '../ui';

export type ErpForm = Record<string, string>;

export const MEDICAL_DEFAULTS: ErpForm = {
  speciality: 'MEDICINA GENERAL',
  slotDurationMinutes: '20',
  businessDaysAhead: '3',
  cooldownDays: '7',
  timeoutMs: '8000',
  // GAP-E — la UI orienta a los tenants nuevos hacia v2 (Tibo Integration API).
  // El schema server-side sigue con default v1 para no romper pilots existentes.
  apiVersion: 'v2',
  slotsPerDayLimit: '6',
  allowedSpecialityIds: '',
};

/**
 * Formulario del ERP de agendamiento médico.
 * Los labels hablan como el operador, no como el schema: "Espera entre citas"
 * en vez de `cooldownDays`, "Horarios por día" en vez de `slotsPerDayLimit`.
 */
export function MedicalErpFields({
  config,
  onChange,
}: {
  config: ErpForm;
  onChange: (next: ErpForm) => void;
}) {
  const set = (key: string, val: string) => onChange({ ...config, [key]: val });
  const get = (key: string) => config[key] ?? MEDICAL_DEFAULTS[key] ?? '';

  return (
    <div data-testid="erp-form-medical-ese-sf" className="space-y-4">
      <div className="toast toast-info">
        <div className="toast-description">
          <span className="font-semibold block mb-1">🏥 Agendamiento médico — SinuSalud</span>
          Conecta el bot con SinuSalud para que los pacientes agenden sus citas por WhatsApp o
          Telegram.
        </div>
      </div>

      <Input
        label="URL del servicio"
        id="medical-base-url"
        placeholder="http://ese-cliente.servisuministros-jg.com"
        value={config.baseUrl ?? ''}
        onChange={(e) => set('baseUrl', e.target.value)}
      />
      <Input
        label="ARN del secreto con el token (opcional en pilotos sin auth)"
        id="medical-secret-arn"
        placeholder="arn:aws:secretsmanager:..."
        value={config.tokenSecretArn ?? ''}
        onChange={(e) => set('tokenSecretArn', e.target.value)}
      />
      <Input
        label="Especialidad por defecto"
        id="medical-speciality"
        placeholder="MEDICINA GENERAL"
        value={get('speciality')}
        onChange={(e) => set('speciality', e.target.value)}
      />

      <div className="grid grid-cols-2 gap-4">
        <Input
          label="Duración de cada cita (min)"
          id="medical-slot-duration"
          type="number"
          placeholder="20"
          value={get('slotDurationMinutes')}
          onChange={(e) => set('slotDurationMinutes', e.target.value)}
        />
        <Input
          label="Días hábiles hacia adelante"
          id="medical-business-days"
          type="number"
          placeholder="3"
          value={get('businessDaysAhead')}
          onChange={(e) => set('businessDaysAhead', e.target.value)}
        />
        <Input
          label="Espera entre citas (días)"
          id="medical-cooldown"
          type="number"
          placeholder="7"
          helper="Días mínimos antes de que el paciente pueda pedir otra."
          value={get('cooldownDays')}
          onChange={(e) => set('cooldownDays', e.target.value)}
        />
        <Input
          label="Tiempo máximo de espera (ms)"
          id="medical-timeout"
          type="number"
          placeholder="8000"
          value={get('timeoutMs')}
          onChange={(e) => set('timeoutMs', e.target.value)}
        />
      </div>

      {/* GAP-E: sin apiVersion=v2 el bot arranca el flow medical-v1 legacy. */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label htmlFor="medical-api-version" className="input-label">
            Versión de la API
          </label>
          <select
            id="medical-api-version"
            className="input input-md w-full"
            value={get('apiVersion')}
            onChange={(e) => set('apiVersion', e.target.value)}
          >
            <option value="v2">v2 — Tibo Integration API (recomendado)</option>
            <option value="v1">v1 — piloto anterior (solo tenants sin migrar)</option>
          </select>
          <p className="text-caption text-neutral-500 dark:text-neutral-400 mt-1">
            Tenants nuevos: v2. v1 solo si el ERP del cliente no fue migrado.
          </p>
        </div>
        <Input
          label="Horarios por día"
          id="medical-slots-per-day-limit"
          type="number"
          placeholder="6"
          helper="Cuántos ofrecerle al paciente por día."
          value={get('slotsPerDayLimit')}
          onChange={(e) => set('slotsPerDayLimit', e.target.value)}
        />
      </div>

      <div>
        <label htmlFor="medical-allowed-speciality-ids" className="input-label">
          Especialidades permitidas (opcional)
        </label>
        <textarea
          id="medical-allowed-speciality-ids"
          className="input input-md w-full min-h-[60px]"
          placeholder="12, 34, 56 (dejar vacío para confiar en el flag bookable del ERP)"
          value={config.allowedSpecialityIds ?? ''}
          onChange={(e) => set('allowedSpecialityIds', e.target.value)}
        />
        <p className="text-caption text-neutral-500 dark:text-neutral-400 mt-1">
          Solo estos IDs aparecen en el flow. Vacío = confiar en el ERP.
        </p>
      </div>
    </div>
  );
}
